/**
 * AI:
 * Status: "in progress"
 * Type: Logic
 * SubType: Resolver
 * Reason: The consultant customer header shows the external aliases per im.ImSystem. The stored
 *   system `url` points at the FKU app of the other tenant (e.g. fku-klaus.alpha-platform.cloud),
 *   but from the consultant shell the customer must open in that tenant's CONSULTANT app
 *   (e.g. co-klaus.alpha-platform.cloud). The exact stored value is not known, so the match is on the
 *   HOST only - with or without scheme, trailing slash or `/#/` suffix, case-insensitive.
 */
import type {CrExternalSystemLinks} from '../service/token/cr-external-system-link.token';

/**
 * The host of a system URL: lower case, without scheme, port, path or hash. A URL without scheme
 * (`fku-klaus.alpha-platform.cloud/#/`) is read as https. Undefined for empty or unparsable input.
 */
export function getCrExternalSystemHost(url: string | null | undefined): string | undefined {

    const trimmed = (url ?? '').trim();

    if (!trimmed) {
        return undefined;
    }

    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    try {
        return new URL(withScheme).hostname.toLowerCase() || undefined;
    } catch {
        return undefined;
    }
}

/**
 * The base URL (without trailing slash) that replaces the system URL, or undefined when the table
 * has no entry for the system's host - then no link is shown.
 */
export function findCrExternalSystemTarget(links: CrExternalSystemLinks | null | undefined,
                                           systemUrl: string | null | undefined): string | undefined {

    const host = getCrExternalSystemHost(systemUrl);

    if (!links || !host) {
        return undefined;
    }

    const key = Object.keys(links).find(candidate => candidate.trim().toLowerCase() === host);
    const base = key ? (links[key] ?? '').trim().replace(/\/+$/, '') : '';

    return base || undefined;
}

/**
 * The customer page of `alias` in the target app: `{base}/#/cr/customers/{alias}`.
 * Undefined when the base or the alias is missing.
 */
export function buildCrExternalCustomerUrl(baseUrl: string | null | undefined,
                                           alias: string | number | null | undefined): string | undefined {

    const base = (baseUrl ?? '').trim().replace(/\/+$/, '');
    const customerAlias = String(alias ?? '').trim();

    if (!base || !customerAlias) {
        return undefined;
    }

    return `${base}/#/cr/customers/${encodeURIComponent(customerAlias)}`;
}
